import { useState, useEffect } from "react";
import { Dialog, DialogPortal, DialogOverlay, DialogClose } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";
import { trackEvent } from "@/utils/analytics";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email address").max(255),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000),
});

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactModal = ({ open, onOpenChange }: ContactModalProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({});
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const contactEmail = "hi@fractionalcpo.com";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(contactEmail);
    trackEvent('email_copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSubmitErrorMessage(null);

    const result = contactSchema.safeParse({ name, email, message });
    if (!result.success) {
      const fieldErrors: { name?: string; email?: string; message?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === "name") fieldErrors.name = err.message;
        if (err.path[0] === "email") fieldErrors.email = err.message;
        if (err.path[0] === "message") fieldErrors.message = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    trackEvent('send_message_click');
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, email, message },
      });

      if (error) throw error;

      trackEvent('contact_form_submitted', { source: 'contact_modal' });
      posthog.identify(email, { name, email });
      setSubmitStatus("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      const anyErr = error as any;
      const bodyError = anyErr?.context?.body?.error;
      setSubmitErrorMessage(
        (typeof bodyError === "string" && bodyError.trim())
          ? bodyError
          : (typeof anyErr?.message === "string" && anyErr.message.trim())
            ? anyErr.message
            : "Unable to send message. Please try again."
      );
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      trackEvent('contact_modal_open');
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSubmitStatus("idle");
      setErrors({});
      setSubmitErrorMessage(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/70 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 w-full max-w-[600px] translate-x-[-50%] translate-y-[-50%] bg-[#002178] border-none shadow-2xl duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
        >
          <div className="p-8 md:p-12">
            {/* Header with title and close button */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[2rem] md:text-[2.5rem] font-light tracking-[-0.02em] text-white leading-none">
                We're here to help
              </h2>
              <DialogPrimitive.Close className="text-white/50 hover:text-white transition-colors focus:outline-none">
                <X className="h-6 w-6" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>
          
            {/* Email with copy */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors group mb-8"
            >
              {copied ? (
                <Check size={16} className="text-green-400" />
              ) : (
                <Copy size={16} />
              )}
              <span className="text-sm">{contactEmail}</span>
            </button>

            {submitStatus === "success" ? (
              <div className="py-8 text-center">
                <p className="text-green-400 text-xl font-medium mb-2">Thanks for reaching out!</p>
                <p className="text-white/70">We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                    className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 rounded-none px-5 h-12 text-base"
                    required
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-2 pl-5">{errors.name}</p>
                  )}
                </div>

                <div>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 rounded-none px-5 h-12 text-base"
                    required
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-2 pl-5">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help?"
                    className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 rounded-none px-5 py-4 min-h-[120px] text-base resize-none"
                    required
                  />
                  {errors.message && (
                    <p className="text-red-400 text-sm mt-2 pl-5">{errors.message}</p>
                  )}
                </div>

                <div className="pt-4">
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-4 bg-white text-[#002178] font-medium transition-transform hover:scale-105 disabled:opacity-50"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </div>

                {submitStatus === "error" && (
                  <p className="text-red-400 text-sm">
                    {submitErrorMessage || "Something went wrong. Please try again."}
                  </p>
                )}

                {/* Trust signals */}
                <div className="pt-6 border-t border-white/10 mt-2">
                  <p className="text-white/40 text-xs italic leading-relaxed mb-3">
                    "Love what you guys are doing since my restructure." <span className="not-italic">— CEO, PE-Backed Platform</span>
                  </p>
                  <div className="flex items-center gap-5 opacity-30">
                    <div className="h-4 w-[90px] flex items-center"><img src="/logos/constant-contact.svg" alt="Constant Contact" className="max-h-full max-w-full object-contain [filter:grayscale(100%)_brightness(0)_invert(1)]" /></div>
                    <div className="h-4 w-[60px] flex items-center"><img src="/logos/shopify-white.svg" alt="Shopify" className="max-h-full max-w-full object-contain" /></div>
                    <div className="h-4 w-[80px] flex items-center"><img src="/logos/mckinsey.svg" alt="McKinsey" className="max-h-full max-w-full object-contain [filter:grayscale(100%)_brightness(0)_invert(1)]" /></div>
                    <div className="h-4 w-[65px] flex items-center"><img src="/logos/instacart.svg" alt="Instacart" className="max-h-full max-w-full object-contain [filter:grayscale(100%)_brightness(0)_invert(1)]" /></div>
                  </div>
                </div>
              </form>
            )}

          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default ContactModal;
